import { computed, Injectable, signal } from '@angular/core';
import { StorageKeys } from 'src/app/configs/storage-keys-config';
import { AddNewMessageToChatCommand } from 'src/app/core/api/models/add-new-message-to-chat-command';
import { ChatBotHistoryItem } from 'src/app/core/api/models/chat-bot-history-item';
import { ChatBotMessageRoles } from 'src/app/core/api/models/chat-bot-message-roles';
import { ChatSubject } from 'src/app/core/api/models/chat-subject';

import { ChatBotMessageItem } from 'src/app/core/models/chat-bot-message-item';
import { NewChatMessageDto } from 'src/app/core/models/new-chat-message-dto';
import { NewChatTitleDto } from 'src/app/core/models/new-chat-title-message-dto';
import { DateTimeService } from 'src/app/core/services/date-time.service';
import { UtilService } from 'src/app/core/services/util.service';

@Injectable({
  providedIn: 'root',
})
export class ChatBotStateService {
  // number of pages already loaded for each chat (1-based count)
  #pagesLoaded = new Map<string, number>();

  //*---------------------- States (Signals)

  #chatHistoryList = signal<ChatBotHistoryItem[]>([]);
  /** لیست چت های موجود در تاریخچه */
  chatHistoryList = computed(() => this.#chatHistoryList());
  /** لیست گروهبندی و مرتب شده (بر اساس تاریخ ایجاد) چت های موجود در تاریخچه  */
  groupedChatHistoryList = computed<ChatHistoryGroupedByDate[]>(() =>
    this.#groupChatHistoryByDate(this.#chatHistoryList()),
  );

  #activeChatMessages = signal<ChatBotMessageItem[]>([]);
  /** لیست تمامی پیام های چت فعال */
  activeChatMessages = computed(() => this.#activeChatMessages());

  #activeChatId = signal<string>(null);
  activeChatId = computed(() => this.#activeChatId());
  activeChat = computed(() =>
    this.#chatHistoryList().find((c) => c.chatId === this.activeChatId()),
  );

  #showChatHistory = signal<boolean>(true);
  showChatHistory = computed(() => this.#showChatHistory());

  #showNewChat = signal<boolean>(true);
  showNewChat = computed(() => this.#showNewChat());

  #initLoading = signal<boolean>(false);
  initLoading = computed(() => this.#initLoading());

  #sending = signal<boolean>(false);
  sending = computed(() => this.#sending());

  #isStreaming = signal<boolean>(false);
  isStreaming = computed(() => this.#isStreaming());

  #chatSubject = signal<ChatSubject>(ChatSubject.General);
  chatSubject = computed(() => this.#chatSubject());

  //*-------------------- Reducers or Actions

  toggleShowChatHistory() {
    this.#showChatHistory.set(!this.#showChatHistory());
  }

  changeShowNewChat(show: boolean) {
    this.#showNewChat.set(show);
    if (show) {
      this.setActiveChatId(null);
      this.setChatMessages([]);
      this.changeChatSubject(ChatSubject.General);
    }
  }

  changeInitLoading(value: boolean) {
    this.#initLoading.set(value);
  }

  changeSending(value: boolean) {
    this.#sending.set(value);
  }

  changeChatSubject(data: ChatSubject) {
    this.#chatSubject.set(data);
  }

  changeIsStreaming(value: boolean) {
    this.#isStreaming.set(value);
  }

  setActiveChatId(chatId) {
    this.#activeChatId.set(chatId || null);
  }

  setChatHistoryList(list) {
    this.#chatHistoryList.set([]);
    this.#chatHistoryList.set(list);
  }

  setChatMessages(chatMsgs: ChatBotMessageItem[]) {
    this.#activeChatMessages.set([]);
    this.#activeChatMessages.set(chatMsgs);
  }

  pushChatMessage(chatMsg: ChatBotMessageItem) {
    const messages = this.#activeChatMessages() || [];
    messages.push(chatMsg);
    this.setChatMessages(messages);
  }

  appendChatHistoryList(newItems: ChatBotMessageItem[]) {
    this.#chatHistoryList.update((old) => [...old, ...newItems]);
  }

  toggleShowChatHistoryInMobileScreen() {
    const width = window.innerWidth;
    if (width <= 768) this.toggleShowChatHistory();
  }

  /** پس از اولین پیام توی تاریخچه چت هم اضافه بشه */
  checkAndAddChatToHistoryLocally(chat: NewChatMessageDto) {
    if (!chat) return;

    const { chatId, chatTitle, created } = chat;

    const chats = this.#chatHistoryList();

    if (chats.find((c) => c.chatId === chat.chatId)) return;

    const savedLastNewChatBotData = localStorage.getItem(
      StorageKeys.lastNewChatBot,
    );

    let subject: ChatSubject = null;

    try {
      if (savedLastNewChatBotData) {
        const lastNewChatBotData: AddNewMessageToChatCommand = JSON.parse(
          savedLastNewChatBotData,
        );
        if (lastNewChatBotData?.chatId === chatId)
          subject = lastNewChatBotData?.subject ?? null;
      }
    } catch {
      subject = null;
    }

    // add chat to history
    const newChat: ChatBotHistoryItem = {
      chatId,
      startDateTime: created,
      title: chatTitle == 'NewChat' ? '' : chatTitle,
      subject,
    };

    localStorage.removeItem(StorageKeys.lastNewChatBot);
    chats.unshift(newChat);
    this.setChatHistoryList(chats);
  }

  /** پیامی که بات داده به لیست پیام ها اضافه کن  */
  addBotMsgToChat(chat: NewChatMessageDto) {
    if (!chat) return;

    const { created, messageId, responseBody, chatId } = chat;

    if (this.activeChatId() && this.activeChatId() !== chatId) return;

    let messages = [...(this.activeChatMessages() || [])];

    const lastMsg = messages[messages?.length - 1];

    if (lastMsg.chatId == null) {
      messages.pop();
      messages.push({
        ...lastMsg,
        chatId,
      });
    } else if (lastMsg?.chatId !== chatId) return;

    // پیدا کن آخرین پیام باتی که در همین چت بوده
    const isMessageContinued = lastMsg?.messageId === messageId;

    // ادامه استریم برای پیام فعلی (هنوز استریم پیام ادامه داره)
    if (isMessageContinued) {
      lastMsg.messageBody += responseBody;
      messages = messages.map((msg) =>
        msg.messageId === messageId ? lastMsg : msg,
      );
    }
    // پیام جدید
    else {
      const botMsg: ChatBotMessageItem = {
        created,
        messageId,
        chatId,
        role: ChatBotMessageRoles.Assistant,
        messageBody: responseBody,
      };
      messages.push(botMsg);
    }

    this.#activeChatMessages.set(messages);
  }

  /**  بصورت لوکال پیامی که کاربر خودش نوشت را به لیست پیام ها اضافه میکنه */
  addUserMsgToChatLocally(messageBody) {
    const messageId = UtilService.getRandomNum();

    const userChat: ChatBotMessageItem = {
      created: DateTimeService.getCurrentDateTime(),
      messageId,
      role: ChatBotMessageRoles.User,
      messageBody,
      chatId: null,
    };

    this.pushChatMessage(userChat);
  }

  /** اگر بک اند تایتل چت را تو خروجی فرستاد اعمالش کن  */
  updateChatTitle(chat: NewChatTitleDto) {
    if (!chat.chatId) return;
    const list = this.#chatHistoryList();
    this.#chatHistoryList.set(
      list.map((item) => {
        if (item.chatId == chat?.chatId)
          return { ...item, title: chat.title } as ChatBotHistoryItem;
        else return item;
      }),
    );
  }

  handleNewChatMessage(chat: NewChatMessageDto) {
    //is streaming
    this.handleChangeIsStreaming(chat);

    // لودینگ ارسال سوال کاربر
    this.changeSending(false);

    // پیامی که بات داده را به لیست پیام ها اضافه کن
    this.addBotMsgToChat(chat);

    //اگر چت تو لیست  تاریخچه نیست به لیست تاریخچه اضافه کن
    // (بیشتر موقع چت جدید اتفاق میوفته)
    this.checkAndAddChatToHistoryLocally(chat);

    // اگر داخل یک چت دیگه نبود بیا این چت را به عنوان چت اکتیو در نظر بگیر
    //(بیشتر موقع چت جدید اتفاق میوفته)
    if (!this.activeChatId()) this.setActiveChatId(chat.chatId);
  }

  handleChangeIsStreaming(chat: NewChatMessageDto) {
    const isDone = chat?.isDone;
    if (this.#activeChatId() !== chat?.chatId) this.changeIsStreaming(false);
    else this.changeIsStreaming(!isDone || false);
  }

  // ------------------ per-chat paging tracking -----------------

  /** get how many pages have been loaded for a chat (0 = none) */
  getPagesLoaded(chatId: string) {
    return this.#pagesLoaded.get(chatId) ?? 0;
  }

  /** increment pages-loaded counter for a chat (default +1) */
  incrPagesLoaded(chatId: string, by = 1) {
    if (!chatId) return;
    const current = this.getPagesLoaded(chatId) || 0;
    this.#pagesLoaded.set(chatId, current + by);
  }

  /** reset pages-loaded for a chat (set to 0) */
  resetPagesLoaded(chatId: string) {
    if (!chatId) return;
    this.#pagesLoaded.set(chatId, 0);
  }

  /** همه وضعیت ها را پاک کن (صدا بزن وقتی کامپوننت اصلی دیستروی شد) */
  clearState() {
    this.setActiveChatId(null);
    this.setChatHistoryList([]);
    this.setChatMessages([]);
    this.changeInitLoading(false);
    this.changeIsStreaming(false);
    this.changeSending(false);
    this.#showChatHistory.set(true);
  }

  /** گروهبندی و مرتب سازی چت های تاریخچه */
  #groupChatHistoryByDate(
    chats: ChatBotHistoryItem[],
  ): ChatHistoryGroupedByDate[] {
    if (!chats?.length) return [];

    const grouped = chats.reduce(
      (acc, item) => {
        const date = item.startDateTime.split('T')[0];
        if (!acc[date]) acc[date] = [];
        acc[date].push(item);
        return acc;
      },
      {} as Record<string, ChatBotHistoryItem[]>,
    );

    // تبدیل به آرایه و مرتب‌سازی بر اساس تاریخ (جدیدترین اول)
    return Object.entries(grouped)
      .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
      .map(([date, items]) => ({ date, items }));
  }
}

export interface ChatHistoryGroupedByDate {
  date?: string;
  items: ChatBotHistoryItem[];
}
